/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import { RegisteredPlayer, Team } from '@sportninja/common/types/Registration'

// Add useDebounce hook
const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface UseSearchAndFilterProps {
  divisions: RegistrationTableDivision[]
}

interface UseSearchAndFilterReturn {
  searchText: string
  setSearchText: (text: string) => void
  selectedDivision: string
  setSelectedDivision: (division: string) => void
  getFilteredTeams: (teams: Team[]) => Team[]
  getFilteredDivisions: () => RegistrationTableDivision[]
  openDivisions: { [key: number]: boolean }
  setOpenDivisions: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >
  allExpanded: boolean
  setAllExpanded: React.Dispatch<React.SetStateAction<boolean>>
  getFilteredPlayers: (players: RegisteredPlayer[]) => RegisteredPlayer[]
  onlyBuckets: boolean
  setOnlyBuckets: React.Dispatch<React.SetStateAction<boolean>>
  selectedStatuses: number[]
  setSelectedStatuses: React.Dispatch<React.SetStateAction<number[]>>
}

// Default statuses (all except Cancelled)
const defaultStatuses = [1, 2, 3, 4] // Submitted, Pending, Approved, Approved w/ conditions

export const useSearchAndFilter = ({
  divisions,
}: UseSearchAndFilterProps): UseSearchAndFilterReturn => {
  const [searchText, setSearchText] = useState('')
  const [selectedDivision, setSelectedDivision] = useState<string>('all')
  const [openDivisions, setOpenDivisions] = useState<{
    [key: number]: boolean
  }>({})
  const [allExpanded, setAllExpanded] = useState(true)
  const [onlyBuckets, setOnlyBuckets] = useState(false)
  const [selectedStatuses, setSelectedStatuses] =
    useState<number[]>(defaultStatuses)

  // Use debounced search text
  const debouncedSearchText = useDebounce(searchText, 300)

  // Initialize all divisions as expanded on first render and when filters change
  useEffect(() => {
    const newOpenDivisions = {}
    divisions.forEach((_, index) => {
      newOpenDivisions[index] = true
    })
    setOpenDivisions(newOpenDivisions)
    setAllExpanded(true)
  }, [divisions, debouncedSearchText, onlyBuckets, selectedStatuses]) // Re-expand when any filter changes

  // Filter teams based on search text, status, and bucket status
  const getFilteredTeams = useCallback(
    (teams: Team[]) => {
      if (!teams) {
        return []
      }

      return teams
        .filter((team) => {
          // If no status filter is applied, keep all teams
          if (selectedStatuses.length === 0) {
            return true
          }

          // Check if team status matches OR if any player status matches
          const teamStatusMatches = selectedStatuses.includes(
            team.registration_status
          )
          const hasMatchingPlayers = team.registered_players?.some((player) =>
            selectedStatuses.includes(player.registration_status)
          )

          return teamStatusMatches || hasMatchingPlayers
        })
        .filter((team) => {
          // Apply bucket filter
          if (onlyBuckets) {
            return team.is_bucket
          }
          return true
        })
        .filter((team) => {
          // Apply search text filter
          if (!debouncedSearchText) {
            return true
          }

          const searchLower = debouncedSearchText.toLowerCase()
          const teamNameMatch = team.name_full
            .toLowerCase()
            .includes(searchLower)
          const hasMatchingPlayer = team.registered_players?.some(
            (player) =>
              player.name_first.toLowerCase().includes(searchLower) ||
              player.name_last.toLowerCase().includes(searchLower) ||
              player.name_full.toLowerCase().includes(searchLower)
          )
          return teamNameMatch || hasMatchingPlayer
        })
        .map((team) => ({
          ...team,
          key: team.id,
        }))
    },
    [debouncedSearchText, onlyBuckets, selectedStatuses]
  )

  // Filter players based on search text and status
  const getFilteredPlayers = useCallback(
    (players: RegisteredPlayer[]) => {
      if (!players) {
        return []
      }

      let filteredPlayers = players

      // Apply status filter
      if (selectedStatuses.length > 0) {
        filteredPlayers = filteredPlayers.filter((player) =>
          selectedStatuses.includes(player.registration_status)
        )
      }

      // Apply search text filter
      if (debouncedSearchText) {
        const searchLower = debouncedSearchText.toLowerCase()
        filteredPlayers = filteredPlayers.filter(
          (player) =>
            player.name_first.toLowerCase().includes(searchLower) ||
            player.name_last.toLowerCase().includes(searchLower) ||
            player.name_full.toLowerCase().includes(searchLower)
        )
      }

      return filteredPlayers.map((player) => ({
        ...player,
        key: player.uid,
      }))
    },
    [debouncedSearchText, selectedStatuses]
  )

  // Filter divisions based on selected division, search text, and status
  const getFilteredDivisions = useCallback(() => {
    if (selectedDivision === 'all') {
      return divisions.filter((division) => {
        // Always show empty divisions
        if (!division.teams || division.teams.length === 0) {
          return true
        }

        // Get filtered teams for this division
        const filteredTeams = getFilteredTeams(division.teams)

        // Get filtered players from all teams
        const allPlayers =
          division.teams?.flatMap((team) => team.registered_players || []) || []
        const filteredPlayers = getFilteredPlayers(allPlayers)

        // Show divisions that have matching teams or players
        return filteredTeams.length > 0 || filteredPlayers.length > 0
      })
    }

    return divisions.filter((division) => {
      const matchesDivisionId = division.id === selectedDivision
      if (!matchesDivisionId) {
        return false
      }

      // Always show empty divisions
      if (!division.teams || division.teams.length === 0) {
        return true
      }

      // Get filtered teams for this division
      const filteredTeams = getFilteredTeams(division.teams)

      // Get filtered players from all teams
      const allPlayers =
        division.teams?.flatMap((team) => team.registered_players || []) || []
      const filteredPlayers = getFilteredPlayers(allPlayers)

      // Show divisions that have matching teams or players
      return filteredTeams.length > 0 || filteredPlayers.length > 0
    })
  }, [divisions, selectedDivision, getFilteredTeams, getFilteredPlayers])

  return {
    searchText,
    setSearchText,
    selectedDivision,
    setSelectedDivision,
    getFilteredTeams,
    getFilteredDivisions,
    openDivisions,
    setOpenDivisions,
    allExpanded,
    setAllExpanded,
    getFilteredPlayers,
    onlyBuckets,
    setOnlyBuckets,
    selectedStatuses,
    setSelectedStatuses,
  }
}
